import { CurrencyCode } from '@/Models/Localization';
import Currency from '@/Models/PrimitiveModels/Currency/Currency';
import StripeService from '@/Services/Subscription/StripeService';
import dayjs from 'dayjs';

export class Subscription {
  id: any;
  status: string;
  plan_id: string;
  plan: Plan;
  new_plan_id: string;
  is_on_invoice: boolean;
  is_cancelled: boolean;
  is_active: boolean;
  ends_at: dayjs.Dayjs | null;
  updated_at: dayjs.Dayjs;
  current_period_end: dayjs.Dayjs;
  instance: string;

  constructor(object: any = {}) {
    this.id = object.id || 0;
    this.status = object?.status || '';
    this.plan_id = object?.plan_id || '';
    this.plan = object?.plan ?? new Plan();
    this.new_plan_id = object?.new_plan_id || '';
    this.is_on_invoice = !!object?.is_on_invoice || false;
    this.is_cancelled = !!object?.is_cancelled || false;
    this.is_active = !!object?.is_active || false;
    this.ends_at = object.ends_at ? dayjs(object.ends_at) : null;
    this.updated_at = dayjs(object?.updated_at);
    this.current_period_end = dayjs(object?.current_period_end);
    this.instance = object?.instance || null;
  }
}

export type PaymentMethod = PaypalPaymentMethod | CardPaymentMethod | SupportedPaymentMethod;

export interface SupportedPaymentMethod {
  type: 'card' | 'paypal' | 'sepa_debit' | 'klarna' | 'pix' | 'bank_transfer';
  is_default: boolean;
}

export interface PaypalPaymentMethod extends SupportedPaymentMethod {
  type: 'paypal';
  email: string;
}

export interface CardPaymentMethod extends SupportedPaymentMethod {
  type: 'card';
  brand: string;
  exp_month: string;
  exp_year: string;
  last4: string;
  is_wallet: string | null;
  wallet_type: string | null;
}

/**
 * Metadata shared by every saved payment method returned by the list / default
 * endpoints: the Stripe payment-method id and optional display fields used by
 * the "Other saved methods" list and the default card.
 *
 * `display_only` is set by the API for synthetic list entries (today: the
 * invoice / bank-transfer arrangement). Those rows are renderable but must
 * never be add/set-default/delete targets.
 */
export interface SavedPaymentMethodMeta {
  id: string;
  created_at?: string | null;
  holder?: string | null;
  display_only?: boolean;
}

export type SavedCardPaymentMethod = CardPaymentMethod & SavedPaymentMethodMeta;
export interface SavedPaypalPaymentMethod extends SupportedPaymentMethod, SavedPaymentMethodMeta {
  type: 'paypal';
  /** From the default-summary endpoint. */
  email?: string;
  /** From the saved-methods list endpoint (`PaymentMethodTransformer`). */
  payer_email?: string | null;
}

export interface SavedSepaPaymentMethod extends SupportedPaymentMethod, SavedPaymentMethodMeta {
  type: 'sepa_debit';
  last4?: string;
}

export interface SavedKlarnaPaymentMethod extends SupportedPaymentMethod, SavedPaymentMethodMeta {
  type: 'klarna';
  email?: string;
  payer_email?: string | null;
}

export interface SavedBankTransferPaymentMethod extends SupportedPaymentMethod, SavedPaymentMethodMeta {
  type: 'bank_transfer';
}

export interface SavedPixPaymentMethod extends SupportedPaymentMethod, SavedPaymentMethodMeta {
  type: 'pix';
}

/**
 * A saved payment method as returned by the list / default endpoints. A
 * discriminated union over the supported method types so the Billing "Payment
 * methods" list can render and de-duplicate each type appropriately.
 *
 * `card`, `paypal`, `klarna` and `pix` are actively-offered (manageable) types;
 * `sepa_debit` and `bank_transfer` are read-only legacy / display-only types
 * (the bank-transfer / invoice arrangement is also surfaced as a synthetic
 * list entry with `display_only: true` via the payment-methods list endpoint).
 */
export type SavedPaymentMethod =
  | SavedCardPaymentMethod
  | SavedPaypalPaymentMethod
  | SavedSepaPaymentMethod
  | SavedKlarnaPaymentMethod
  | SavedBankTransferPaymentMethod
  | SavedPixPaymentMethod;

/** Method types that can be added / set-default / deleted from the Billing UI. */
export type ManageablePaymentMethodType = 'card' | 'paypal' | 'klarna' | 'pix';

/**
 * True when a saved method may be managed (add/set-default/delete) by an owner.
 * Synthetic `display_only` entries from the API are never manageable, even if
 * their `type` would otherwise be in the manageable allowlist.
 */
export function isManageablePaymentMethod(
  method: Pick<SavedPaymentMethod, 'type'> & { display_only?: boolean }
): boolean {
  if (method.display_only === true) {
    return false;
  }

  return method.type === 'card' || method.type === 'paypal' || method.type === 'klarna' || method.type === 'pix';
}

// Monthly plans live on their own Stripe products with their own planName, so events and BI can tell them
// apart from the annual plan; this maps them back to the tier they belong to.
const TIER_BY_MONTHLY_PLAN_NAME: { [planName: string]: string } = {
  Flex: 'Starter',
  AdvancedMonthly: 'Advanced',
};

export class Plan {
  id: any;
  title: string;
  subtitle: string;
  codeLimit: string;
  scanLimit: string;
  product_id: string;
  bulkLimit: string | null;
  apiLimit: string | null;
  userLimit: string;
  planLevel: number | string;
  hasPremiumSupport: boolean;
  popular: boolean;
  hidden: boolean;
  isEnterprisePlan: boolean;
  isStarterPlan: boolean;
  isMonthlyPlan: boolean;
  oldPlanId: string;
  interval: string; // 'year' | 'month'
  interval_count: number;
  one_time_fee_price_id: string;
  currency: CurrencyCode;
  originalPriceMonthly: Currency;
  price: Currency;
  displayOriginalPrice: Currency;
  translationKey: string;
  planName: string;
  tierName: string; // The tier a plan belongs to, e.g. "AdvancedMonthly" → "Advanced"
  strikethrough_price?: string | null;

  constructor(object: any = {}) {
    this.id = object?.id || '';
    this.title = object?.title || '';
    this.subtitle = object?.subtitle || '';
    this.codeLimit = object?.codeLimit || '';
    this.scanLimit = object?.scanLimit || '';
    this.product_id = object?.product_id || '';
    this.bulkLimit = object?.bulkLimit || null;
    this.apiLimit = object?.apiLimit || null;
    this.userLimit = object?.userLimit || '';
    this.planLevel = object?.planLevel || -1; // not subscribed - TODO: plan level enum
    this.hasPremiumSupport = !!object?.hasPremiumSupport || false;
    this.popular = !!object?.popular || false;
    this.hidden = !!object?.hidden || false;
    this.isEnterprisePlan = !!object?.isEnterprisePlan || false;
    this.oldPlanId = object?.oldPlanId || '';
    this.interval = object?.interval || '';
    this.interval_count = object?.interval_count || 1;
    this.one_time_fee_price_id = object?.one_time_fee_price_id || '';
    this.currency = object?.currency || '';
    this.isStarterPlan = (!!object?.planName && object?.planName === 'Starter') || false;
    this.isMonthlyPlan = this.interval === 'month' && this.interval_count === 1;

    this.originalPriceMonthly = object?.originalPriceMonthly?.price
      ? object.originalPriceMonthly
      : new Currency(object?.originalPriceMonthly || 0);

    this.price = object?.price?.price ? object.price : new Currency(object?.price || 0);

    this.displayOriginalPrice = object?.displayOriginalPrice?.price
      ? object.displayOriginalPrice
      : new Currency(object?.displayOriginalPrice || 0);

    this.translationKey = object?.translationKey || '';
    this.planName = object?.planName || '';
    this.tierName = TIER_BY_MONTHLY_PLAN_NAME[this.planName] ?? this.planName;
    // Used for showing the discounted price on the checkout page summary section
    this.strikethrough_price = object?.strikethrough_price || null;
  }

  // Is this the monthly plan of the given annual plan's tier, e.g. AdvancedMonthly of Advanced?
  // Compared by tier name, not plan level: Flex (level 0) is the monthly plan of Starter (level 1).
  isMonthlyVariantOf(plan: Plan): boolean {
    return plan.interval === 'year' && this.isMonthlyPlan && this.id !== plan.id && this.tierName === plan.tierName;
  }
}

export interface LoadingState {
  loadingSubscription: boolean;
  cancelSubscription: boolean;
  reactivateSubscription: boolean;
  loadingPlans: boolean;
}

export class Invoice {
  id: any = 0;
  amount_due: number = 0;
  amount_paid: number = 0;
  amount_remaining: number = 0;
  created: number = 0;
  currency: string = '';
  hosted_invoice_url: string = '';
  invoice_pdf: string = '';
  number: string = '';
  original_status: string = '';
  paid: boolean = true;
  plan: string = '';
  planProductId: string = '';
  provider: string = '';
  status: string = '';

  constructor(object: any = {}) {
    this.id = object?.id || 0;
    this.amount_due = object?.amount_due || 0;
    this.amount_paid = object?.amount_paid || 0;
    this.amount_remaining = object?.amount_remaining || 0;
    this.created = object?.created || 0;
    this.currency = object?.currency || '';
    this.hosted_invoice_url = object?.hosted_invoice_url || '';
    this.invoice_pdf = object?.invoice_pdf || '';
    this.number = object?.number || '';
    this.original_status = object?.original_status || '';
    this.paid = !!object?.paid || true;
    this.plan = object?.plan || '';
    this.planProductId = object?.plan_product_id || '';
    this.provider = object?.provider || '';
    this.status = object?.status || '';
  }
}

export class SubscriptionState {
  activeSubscription: Subscription = new Subscription();
  activePlan: Plan = new Plan();
  plans: Array<Plan> = [];
  stripeService: StripeService | null = null;
  selectedPlan: Plan = new Plan(); // Just a helper for plan selection NOT the active plan from subscription
  isBankTransferSelected: boolean = false;
  paymentMethods: SavedPaymentMethod[] = [];
  loadingPaymentMethods: boolean = false;
  retryingPaymentMethods: boolean = false;
  paymentMethodsError: boolean = false;
  // KBPE-46: true when this checkout session was entered via the post-signup
  // paid-intent redirect from the classic app's /oauth/cognito (?ref=marketing_signup).
  isPaidIntentSignup: boolean = false;
  showLoading: LoadingState = {
    loadingSubscription: false,
    cancelSubscription: false,
    reactivateSubscription: false,
    loadingPlans: false,
  };
}
